import { ExternalLink } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { CARD_WALL_RADIUS, clickableCardProps } from "@/shared/ui/cardWall";
import { cn } from "@/shared/lib/utils";

import type { TemplateSummary } from "../../api/types";
import { featureLabel } from "../../model/schemaLabels";
import { TemplateCapabilityChips } from "./TemplateCapabilities";

/**
 * 屏 2 一张模板卡:缩略图占位 + 名称 / 已选中 + 描述 + chips(code / 页数 / 区块数 / 特性)+ 两个按钮。
 *
 * ★ 整卡可点 = 选中(右栏切到它的清单),`clickableCardProps` 补齐键盘可达;
 *   卡里的按钮各自 stopPropagation,免得点「用这套建站」先触发一次选中再开弹窗(视觉上抖一下)。
 * ★ 「演示站」此刻没有可打开的东西(演示站要发布服务器接入),画成 disabled 并写明原因。
 * ★ 缩略图:有 `thumbnailUrl`(1009 V19:演示站首页截图)就显示截图,加载失败退回占位;没有时不编一屏假首页文案,只有渐变 + 模板名。
 * ★ 宿主模板(`hostTemplate`,A.46 / A.57):chips 换成「分类 / N 页 / 可改 N 处 /(dynamic)带产品详情页 /(articleList)新闻自动更新」,不显示 uuid
 *   模板码、区块数、特性(宿主模板都没有);`previewUrl`(演示站)有则「看演示」是真外链,没有就禁用并说明。
 */
export function TemplateCard({
  template,
  selected,
  onSelect,
  onUse,
}: {
  template: TemplateSummary;
  selected: boolean;
  onSelect: () => void;
  onUse: () => void;
}) {
  return (
    <div
      {...clickableCardProps(onSelect)}
      aria-pressed={selected}
      aria-label={`模板:${template.name}`}
      className={cn(
        "flex cursor-pointer flex-col overflow-hidden border bg-card text-card-foreground shadow-sm transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        selected
          ? "border-primary ring-1 ring-primary/40"
          : "border-border hover:border-primary/50",
        CARD_WALL_RADIUS
      )}
    >
      <TemplateThumb name={template.name} src={template.thumbnailUrl ?? null} />
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-semibold text-foreground">{template.name}</h3>
          {selected && <Badge className="normal-case tracking-normal">已选中</Badge>}
        </div>
        <p className="text-xs leading-relaxed text-muted-foreground">{template.description}</p>
        {template.hostTemplate ? (
          <HostChips template={template} />
        ) : (
          <ul className="flex flex-wrap gap-1.5 text-2xs" aria-label="模板要点">
            <li className="rounded border border-border px-1.5 py-0.5 font-mono text-muted-foreground">
              {template.code}
            </li>
            <li className="rounded border border-border px-1.5 py-0.5 text-muted-foreground">
              {template.pages.length} 页
            </li>
            <li className="rounded border border-border px-1.5 py-0.5 text-muted-foreground">
              {template.blockTypes.length} 种区块
            </li>
            {template.features.map((f) => (
              <li key={f} className="rounded bg-primary/10 px-1.5 py-0.5 text-primary">
                {featureLabel(f)}
              </li>
            ))}
          </ul>
        )}
        <div className="mt-auto flex items-center gap-2 pt-1">
          <Button
            size="sm"
            variant={selected ? "default" : "outline"}
            onClick={(e) => {
              e.stopPropagation();
              onUse();
            }}
          >
            用这套建站
          </Button>
          {template.hostTemplate ? (
            <DemoLink href={template.previewUrl} />
          ) : (
            <Button
              size="sm"
              variant="ghost"
              disabled
              title="演示站待发布服务器接入"
              onClick={(e) => e.stopPropagation()}
            >
              演示站
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/** 卡片头:截图(顶端对齐裁切,看到的是首屏)或渐变占位;截图加载失败 → 占位。 */
function TemplateThumb({ name, src }: { name: string; src: string | null }) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showImage = !!src && failedSrc !== src;
  if (showImage) {
    return (
      <div className="h-28 overflow-hidden border-b border-border bg-muted">
        <img
          src={src}
          alt={`${name} 演示站首页截图`}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover object-top"
          onError={() => setFailedSrc(src)}
        />
      </div>
    );
  }
  return (
    <div
      aria-hidden
      data-testid="template-thumb-placeholder"
      className="flex h-28 items-end bg-gradient-to-br from-primary/15 via-primary/5 to-muted p-3"
    >
      <span className="text-sm font-semibold text-foreground/70">{name}</span>
    </div>
  );
}

const CHIP = "rounded border border-border px-1.5 py-0.5 text-muted-foreground";

function HostChips({ template }: { template: TemplateSummary }) {
  return (
    <ul className="flex flex-wrap gap-1.5 text-2xs" aria-label="模板要点">
      <li className={CHIP}>{template.family}</li>
      <li className={CHIP}>{template.pages.length} 页</li>
      <li className={CHIP}>可改 {template.fieldCount} 处</li>
      <TemplateCapabilityChips template={template} />
    </ul>
  );
}

/** 宿主模板的演示站(1009 V19 登记的演示站):有就新开页,没有禁用并说明。 */
function DemoLink({ href }: { href: string | null }) {
  if (!href) {
    return (
      <Button
        size="sm"
        variant="ghost"
        disabled
        title="这套模板暂无演示站"
        onClick={(e) => e.stopPropagation()}
      >
        看演示
      </Button>
    );
  }
  return (
    <Button asChild size="sm" variant="ghost">
      <a href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
        <ExternalLink />
        看演示
      </a>
    </Button>
  );
}
